import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { getSeo, normalizeLocale } from '../seo/seoRoutes.mjs';

/**
 * Keeps the tab title and meta description correct during client-side
 * navigation.
 *
 * Division of labour with functions/_middleware.js:
 *   - The middleware owns the ENTRY document. It emits title, description,
 *     canonical, hreflang, robots, OG/Twitter and JSON-LD into the HTML before
 *     it ever reaches the browser, which is what crawlers and social scrapers
 *     read (social scrapers never execute JS, so there is no point mutating
 *     OG tags here).
 *   - This hook only covers SPA navigation, where the URL changes without a
 *     fetch and the browser tab / history entry would otherwise go stale.
 *     It also matters for screen readers, which announce the title on route
 *     change.
 *
 * This replaces MetaUpdater.js, which keyed its effect on language ONLY. That
 * made every route report the homepage's title and description after hydration,
 * silently undoing the per-route metadata for Googlebot (which indexes the
 * rendered DOM).
 */
const useSeoMeta = () => {
  const location = useLocation();
  const { currentLanguage } = useLanguage();

  useEffect(() => {
    const locale = normalizeLocale(currentLanguage);
    const seo = getSeo(location.pathname, locale);
    if (!seo) return; // unmatched route: leave whatever the edge served in place

    if (seo.title) {
      document.title = seo.title;
    }

    if (seo.description) {
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute('content', seo.description);
      }
    }
  }, [location.pathname, currentLanguage]);
};

export default useSeoMeta;
